import { useEffect, useState } from "react";

/**
 * Full-screen animated splash shown at app start.
 * Auto-hides after ~1.8s. Uses a soft fade-out so the app underneath appears smoothly.
 */
export default function SplashScreen({ onDone }) {
  const [phase, setPhase] = useState("visible"); // visible → fading → hidden

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("fading"), 1600);
    const t2 = setTimeout(() => {
      setPhase("hidden");
      onDone?.();
    }, 2100);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [onDone]);

  if (phase === "hidden") return null;

  return (
    <div
      className={`fixed inset-0 z-[100] flex flex-col items-center justify-center transition-opacity duration-500 ${
        phase === "fading" ? "opacity-0" : "opacity-100"
      }`}
      style={{
        background:
          "radial-gradient(circle at 50% 45%, #0F2E4E 0%, #0A2540 45%, #051426 100%)",
        fontFamily: "Outfit, Inter, sans-serif",
      }}
      data-testid="splash-screen"
    >
      {/* Rings */}
      <div className="relative">
        <div className="absolute inset-0 rounded-full bg-[#FACC15]/25 blur-3xl scale-150" />
        <div className="absolute inset-0 rounded-full bg-[#FACC15]/20 animate-ping" />
        <div className="absolute inset-0 rounded-full bg-[#FACC15]/15 animate-pulse" style={{ animationDelay: "0.4s" }} />
        <div className="relative w-32 h-32 rounded-full bg-white shadow-2xl flex items-center justify-center overflow-hidden splash-scale">
          <img src="/trigo-logo.png" alt="TrigoTour" className="w-24 h-24 object-contain" />
        </div>
      </div>

      {/* Wordmark */}
      <h1
        className="mt-8 text-4xl font-extrabold tracking-tight text-white splash-slide-up"
        style={{ fontFamily: "Outfit" }}
      >
        Trigo<span className="text-[#FACC15]">Tour</span>
      </h1>
      <p className="mt-2 text-[11px] uppercase tracking-[0.3em] font-bold text-[#FACC15] splash-slide-up-delay">
        Cameroun · Mobilité
      </p>

      {/* Loader dots */}
      <div className="mt-10 flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-[#FACC15] splash-dot" style={{ animationDelay: "0s" }} />
        <span className="w-2 h-2 rounded-full bg-[#FACC15] splash-dot" style={{ animationDelay: "0.2s" }} />
        <span className="w-2 h-2 rounded-full bg-[#FACC15] splash-dot" style={{ animationDelay: "0.4s" }} />
      </div>

      <style>{`
        @keyframes splashScale {
          0% { transform: scale(0.6); opacity: 0; }
          60% { transform: scale(1.08); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .splash-scale { animation: splashScale 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
        @keyframes splashSlideUp {
          from { transform: translateY(12px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .splash-slide-up { animation: splashSlideUp 0.6s ease-out 0.4s both; }
        .splash-slide-up-delay { animation: splashSlideUp 0.6s ease-out 0.55s both; }
        @keyframes splashDot {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-6px); opacity: 1; }
        }
        .splash-dot { animation: splashDot 1.1s infinite ease-in-out; }
      `}</style>
    </div>
  );
}
